// Holiday and event utilities for calendar display

interface HolidayEvent {
  title: string;
  start: string;
  end?: string;
  type: 'jewish' | 'us' | 'event';
}

// Jewish holidays - dates for 2024-2027 (Hebrew calendar varies each year)
const jewishHolidays: Record<number, Array<{ name: string; start: string; end?: string }>> = {
  2024: [
    { name: 'Passover', start: '2024-04-22', end: '2024-04-30' },
    { name: 'Shavuot', start: '2024-06-11', end: '2024-06-13' },
    { name: 'Rosh Hashanah', start: '2024-10-02', end: '2024-10-04' },
    { name: 'Yom Kippur', start: '2024-10-11', end: '2024-10-12' },
    { name: 'Sukkot', start: '2024-10-16', end: '2024-10-23' },
    { name: 'Hanukkah', start: '2024-12-25', end: '2025-01-02' },
  ],
  2025: [
    { name: 'Passover', start: '2025-04-12', end: '2025-04-20' },
    { name: 'Shavuot', start: '2025-06-01', end: '2025-06-03' },
    { name: 'Rosh Hashanah', start: '2025-09-22', end: '2025-09-24' },
    { name: 'Yom Kippur', start: '2025-10-01', end: '2025-10-02' },
    { name: 'Sukkot', start: '2025-10-06', end: '2025-10-13' },
    { name: 'Hanukkah', start: '2025-12-14', end: '2025-12-22' },
  ],
  2026: [
    { name: 'Passover', start: '2026-04-01', end: '2026-04-09' },
    { name: 'Shavuot', start: '2026-05-21', end: '2026-05-23' },
    { name: 'Rosh Hashanah', start: '2026-09-11', end: '2026-09-13' },
    { name: 'Yom Kippur', start: '2026-09-20', end: '2026-09-21' },
    { name: 'Sukkot', start: '2026-09-25', end: '2026-10-02' },
    { name: 'Hanukkah', start: '2026-12-04', end: '2026-12-12' },
  ],
  2027: [
    { name: 'Passover', start: '2027-04-21', end: '2027-04-29' },
    { name: 'Shavuot', start: '2027-06-10', end: '2027-06-12' },
    { name: 'Rosh Hashanah', start: '2027-10-01', end: '2027-10-03' },
    { name: 'Yom Kippur', start: '2027-10-10', end: '2027-10-11' },
    { name: 'Sukkot', start: '2027-10-15', end: '2027-10-22' },
    { name: 'Hanukkah', start: '2027-12-24', end: '2028-01-01' },
  ],
};

// US Open tennis dates (typically late Aug - early Sep)
const usOpenDates: Record<number, { start: string; end: string }> = {
  2024: { start: '2024-08-26', end: '2024-09-08' },
  2025: { start: '2025-08-25', end: '2025-09-07' },
  2026: { start: '2026-08-31', end: '2026-09-13' },
  2027: { start: '2027-08-30', end: '2027-09-12' },
};

// NYC Marathon dates (first Sunday in November)
const nycMarathonDates: Record<number, string> = {
  2024: '2024-11-03',
  2025: '2025-11-02',
  2026: '2026-11-01',
  2027: '2027-11-07',
};

// Helper to get nth weekday of month
function getNthWeekday(year: number, month: number, weekday: number, n: number): Date {
  const date = new Date(year, month, 1);
  let count = 0;
  while (count < n) {
    if (date.getDay() === weekday) {
      count++;
      if (count === n) break;
    }
    date.setDate(date.getDate() + 1);
  }
  return date;
}

// Helper to get last weekday of month
function getLastWeekday(year: number, month: number, weekday: number): Date {
  const date = new Date(year, month + 1, 0); // Last day of month
  while (date.getDay() !== weekday) {
    date.setDate(date.getDate() - 1);
  }
  return date;
}

// Format date as YYYY-MM-DD
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Get US holidays for a given year
function getUSHolidays(year: number): Array<{ name: string; date: string }> {
  return [
    { name: "New Year's Day", date: `${year}-01-01` },
    { name: 'MLK Day', date: formatDate(getNthWeekday(year, 0, 1, 3)) }, // 3rd Monday Jan
    { name: "Presidents' Day", date: formatDate(getNthWeekday(year, 1, 1, 3)) }, // 3rd Monday Feb
    { name: 'Memorial Day', date: formatDate(getLastWeekday(year, 4, 1)) }, // Last Monday May
    { name: 'Independence Day', date: `${year}-07-04` },
    { name: 'Labor Day', date: formatDate(getNthWeekday(year, 8, 1, 1)) }, // 1st Monday Sep
    { name: 'Columbus Day', date: formatDate(getNthWeekday(year, 9, 1, 2)) }, // 2nd Monday Oct
    { name: 'Veterans Day', date: `${year}-11-11` },
    { name: 'Thanksgiving', date: formatDate(getNthWeekday(year, 10, 4, 4)) }, // 4th Thursday Nov
    { name: 'Christmas', date: `${year}-12-25` },
  ];
}

// Generate all holiday events for a date range
export function getHolidayEvents(startDate: string, endDate: string): HolidayEvent[] {
  const events: HolidayEvent[] = [];
  const startYear = new Date(startDate).getFullYear();
  const endYear = new Date(endDate).getFullYear();

  for (let year = startYear; year <= endYear; year++) {
    // US Holidays
    const usHolidays = getUSHolidays(year);
    for (const holiday of usHolidays) {
      if (holiday.date >= startDate && holiday.date <= endDate) {
        events.push({
          title: holiday.name,
          start: holiday.date,
          type: 'us',
        });
      }
    }

    // Jewish Holidays
    const jewish = jewishHolidays[year];
    if (jewish) {
      for (const holiday of jewish) {
        if (holiday.start <= endDate && (holiday.end || holiday.start) >= startDate) {
          events.push({
            title: holiday.name,
            start: holiday.start,
            end: holiday.end,
            type: 'jewish',
          });
        }
      }
    }

    // US Open
    const usOpen = usOpenDates[year];
    if (usOpen && usOpen.start <= endDate && usOpen.end >= startDate) {
      events.push({
        title: 'US Open',
        start: usOpen.start,
        end: usOpen.end,
        type: 'event',
      });
    }

    // NYC Marathon
    const marathon = nycMarathonDates[year];
    if (marathon && marathon >= startDate && marathon <= endDate) {
      events.push({
        title: 'NYC Marathon',
        start: marathon,
        type: 'event',
      });
    }
  }

  return events;
}

// Convert holiday events to FullCalendar background events
export function getHolidayBackgroundEvents(startDate: string, endDate: string) {
  const holidays = getHolidayEvents(startDate, endDate);

  return holidays.map((holiday, index) => {
    // Add one day to end date for FullCalendar's exclusive end handling
    let end = holiday.end || holiday.start;
    const endDateObj = new Date(end);
    endDateObj.setDate(endDateObj.getDate() + 1);
    end = endDateObj.toISOString().split('T')[0];

    return {
      id: `holiday-${index}`,
      title: holiday.title,
      start: holiday.start,
      end,
      display: 'background',
      classNames: [`holiday-${holiday.type}`],
      extendedProps: {
        isHoliday: true,
        holidayType: holiday.type,
      },
    };
  });
}
